import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type PlaceOrderRequest, tradingService } from '@/services/trading';
import { riskService } from '@/services/risk';

export function useTrading() {
  const queryClient = useQueryClient();

  const ordersQuery = useQuery({
    queryKey: ['orders'],
    queryFn: () => tradingService.getOrders({ limit: 50 }),
    refetchInterval: 10000,
  });

  const marketStatusQuery = useQuery({
    queryKey: ['market', 'status'],
    queryFn: () => tradingService.getMarketStatus(),
    refetchInterval: 60000,
  });

  const placeOrderMutation = useMutation({
    mutationFn: (req: PlaceOrderRequest) => tradingService.placeOrder(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['risk'] });
    },
  });

  const cancelOrderMutation = useMutation({
    mutationFn: (id: string) => tradingService.cancelOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const overrideRiskMutation = useMutation({
    mutationFn: (id: string) => tradingService.overrideRiskWarning(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['risk'] });
    },
  });

  const validateOrderMutation = useMutation({
    mutationFn: (order: {
      symbol: string;
      quantity: number;
      price: number;
      side: 'buy' | 'sell';
    }) => riskService.validateOrder(order),
  });

  return {
    orders: ordersQuery.data || [],
    isLoadingOrders: ordersQuery.isLoading,
    marketStatus: marketStatusQuery.data,
    placeOrder: placeOrderMutation.mutate,
    placeOrderAsync: placeOrderMutation.mutateAsync,
    isPlacingOrder: placeOrderMutation.isPending,
    placeOrderResult: placeOrderMutation.data,
    placeOrderError: placeOrderMutation.error,
    cancelOrder: cancelOrderMutation.mutate,
    isCancelling: cancelOrderMutation.isPending,
    overrideRisk: overrideRiskMutation.mutate,
    overrideRiskAsync: overrideRiskMutation.mutateAsync,
    isOverridingRisk: overrideRiskMutation.isPending,
    validateOrder: validateOrderMutation.mutate,
    validateOrderAsync: validateOrderMutation.mutateAsync,
    validationResult: validateOrderMutation.data,
    isValidating: validateOrderMutation.isPending,
  };
}

export default useTrading;
